import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "horizontal" | "featured";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative w-full h-[420px] md:h-[500px] rounded-xl overflow-hidden cursor-pointer"
      >
        <img
          src={article.coverImage}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-full">
              {article.category}
            </span>
            {article.isHot && (
              <span className="px-2 py-1 bg-accent-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                <i className="ri-fire-line text-xs"></i>热门
              </span>
            )}
          </div>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-white mb-2 group-hover:text-primary-300 transition-colors line-clamp-2">
            {article.title}
          </h2>
          <p className="text-sm text-white/70 line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-4 text-xs text-white/50">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.publishDate}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-28 h-20 md:w-36 md:h-24 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-col justify-between min-w-0 flex-1">
          <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="text-primary-600 font-medium">{article.category}</span>
            <span>{article.publishDate}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative w-full h-48 md:h-52 overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 bg-primary-500 text-white text-xs font-medium rounded-full">
            {article.category}
          </span>
        </div>
        {article.isHot && (
          <div className="absolute top-3 right-3">
            <span className="px-2 py-1 bg-accent-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
              <i className="ri-fire-line text-xs"></i>热
            </span>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2 leading-snug">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
          </span>
          <span>{article.publishDate}</span>
        </div>
      </div>
    </Link>
  );
}